import axios from "axios";
import jsonwebtoken from "jsonwebtoken";

import {
  APIS,
  EnvKey,
  navigation,
} from "./const";

import store, {
  userAction,
} from "../Redux/store";

const isBrowser = () =>
  typeof window !== "undefined";
let csrfToken = null;
let csrfExpiresAt = 0;
let securityPromise = null;

const getSecurityContext =
  async () => {
    if (!isBrowser()) {
      return null;
    }

    const now = Date.now();

    if (
      csrfToken &&
      csrfExpiresAt >
        now + 30000
    ) {
      return csrfToken;
    }

    if (securityPromise) {
      return securityPromise;
    }

    securityPromise = fetch(
      "/api/security/session",
      {
        method: "GET",

        credentials:
          "same-origin",

        headers: {
          Accept:
            "application/json",
        },
      },
    )
      .then(
        async (response) => {
          if (!response.ok) {
            const errorData =
              await response
                .json()
                .catch(
                  () => null,
                );

            throw new Error(
              errorData?.message ||
                "Request initialization failed",
            );
          }

          const data =
            await response.json();

          if (!data?.token) {
            throw new Error(
              "Request initialization failed",
            );
          }

          csrfToken =
            data.token;

          csrfExpiresAt =
            data.expiresAt || 0;

          return csrfToken;
        },
      )
      .finally(() => {
        securityPromise = null;
      });

    return securityPromise;
  };

export const api =
  axios.create({
    withCredentials: true,
  });

api.interceptors.request.use(
  async (config) => {
    config.headers =
      config.headers || {};

    if (isBrowser()) {
      config.baseURL =
        APIS.baseURL;

      const securityToken =
        await getSecurityContext();

      config.headers[
        "x-csrf-token"
      ] =
        securityToken;

      let Token = "";

      const EncodedToken =
        localStorage.getItem(
          "UT_UI",
        );

      if (EncodedToken) {
        try {
          const SessionData =
            jsonwebtoken.verify(
              EncodedToken,
              EnvKey.secretKey,
            );

          Token =
            SessionData.token;
        } catch {
          localStorage.removeItem(
            "UT_UI",
          );
        }
      }

      if (Token) {
        config.headers[
          "Authorization"
        ] =
          `Bearer ${Token}`;
      }

      const state =
        store.getState();

      const locale =
        state.lang?.locale;

      if (locale) {
        config.headers[
          "accept-language"
        ] =
          locale;
      }

      config.headers[
        "x-source-type"
      ] =
        config.headers[
          "x-source-type"
        ] ||
        "creditech";

      config.headers[
        "x-version"
      ] = "200";

      config.headers[
        "Cache-Control"
      ] =
        "no-store, no-cache, must-revalidate, proxy-revalidate";

      config.headers[
        "Pragma"
      ] =
        "no-cache";

      config.headers[
        "Expires"
      ] = "0";
    }

    else {
      const internalProxyUrl =
        process.env
          .INTERNAL_PROXY_URL;

      const internalSecret =
        process.env
          .INTERNAL_PROXY_SECRET;

      if (!internalProxyUrl) {
        throw new Error(
          "INTERNAL_PROXY_URL is missing",
        );
      }

      if (!internalSecret) {
        throw new Error(
          "INTERNAL_PROXY_SECRET is missing",
        );
      }
      config.baseURL =
        internalProxyUrl;

      config.headers[
        "x-internal-proxy-key"
      ] =
        internalSecret;

      config.headers[
        "x-source-type"
      ] =
        config.headers[
          "x-source-type"
        ] ||
        "dashboard";
    }

    config.headers[
      "Content-Type"
    ] =
      config.headers[
        "Content-Type"
      ] ||
      "application/json";

    config.headers[
      "Accept"
    ] =
      config.headers[
        "Accept"
      ] ||
      "application/json";

    return config;
  },

  (error) =>
    Promise.reject(error),
);


api.interceptors.response.use(
  (response) => {
    return response;
  },

  async (error) => {
    const originalRequest =
      error?.config;

    if (!isBrowser()) {
      return Promise.reject(
        error,
      );
    }

    if (!originalRequest) {
      return Promise.reject(
        error,
      );
    }

   
    const isSecurityError =
      error?.response?.status === 403 &&
      error?.response?.data?.code ===
        "ACCESS_DENIED";

    if (
      isSecurityError &&
      !originalRequest._securityRetry
    ) {
      originalRequest._securityRetry =
        true;

      csrfToken = null;
      csrfExpiresAt = 0;
      securityPromise = null;

      try {
       
        const newToken =
          await getSecurityContext();

        originalRequest.headers =
          originalRequest.headers ||
          {};

        originalRequest.headers[
          "x-csrf-token"
        ] = newToken;

      
        return api(
          originalRequest,
        );
      } catch (
        securityError
      ) {
        return Promise.reject(
          securityError,
        );
      }
    }

    if (isSecurityError) {
      return Promise.reject(
        error,
      );
    }


    if (
      error?.response?.status !==
        401 ||
      originalRequest?._retry
    ) {
      return Promise.reject(
        error,
      );
    }

    originalRequest._retry =
      true;

    const encodedToken =
      localStorage.getItem(
        "UT_UI",
      );

    if (!encodedToken) {
      return Promise.reject(
        error,
      );
    }

    try {
      const {
        id,
        refreshToken,
        phone,
      } =
        jsonwebtoken.verify(
          encodedToken,
          EnvKey.secretKey,
        );

      localStorage.removeItem(
        "UT_UI",
      );

      const res =
        await api.post(
          "/auth/refresh",
          {
            refreshToken,
            phone,
          },
          {
            headers: {
              "x-source-type":
                "creditech",
            },
          },
        );

      const userData =
        jsonwebtoken.sign(
          {
            token:
              res?.data
                ?.accessToken,

            refreshToken:
              res?.data
                ?.refreshToken,

            id,
            phone,
          },
          EnvKey.secretKey,
        );

      localStorage.setItem(
        "UT_UI",
        userData,
      );

      originalRequest.headers =
        originalRequest.headers ||
        {};

      originalRequest.headers[
        "Authorization"
      ] =
        `Bearer ${
          res?.data
            ?.accessToken
        }`;

      originalRequest.headers[
        "x-source-type"
      ] =
        originalRequest.headers[
          "x-source-type"
        ] ||
        "creditech";

      return api(
        originalRequest,
      );
    } catch (
      refreshError
    ) {
      localStorage.removeItem(
        "UT_UI",
      );

      store.dispatch(
        userAction.update({
          key: "name",
          val: "",
        }),
      );

      store.dispatch(
        userAction.update({
          key: "avatar",
          val: "",
        }),
      );

      store.dispatch(
        userAction.update({
          key: "nationalId",
          val: "",
        }),
      );

      navigation.router.push(
        "/",
      );

      return Promise.reject(
        refreshError,
      );
    }
  },
);

export default api;